import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Table, Button, Input, Select, Space, Tag, Card, Statistic, Row, Col, Modal, Form, message, Drawer, Descriptions } from 'antd'
import { PlusOutlined, EyeOutlined, StopOutlined, PlayCircleOutlined, SettingOutlined, WarningOutlined, UserOutlined, MailOutlined, PhoneOutlined, EnvironmentOutlined } from '@ant-design/icons'
import { api } from '../services/api'

const { Search } = Input
const { Option } = Select

interface Business {
  id: string
  name: string
  email?: string
  phone?: string
  website?: string
  address?: string | any
  city?: string
  state?: string
  zipCode?: string
  country?: string
  industryType?: string
  companyType?: string
  serviceCategories?: string[]
  subscriptionPlan: string
  subscriptionStatus: string
  subscriptionEndsAt?: string
  userLimit: number
  isActive: boolean
  createdAt: string
  updatedAt: string
  hasBouncedEmails?: boolean
  _count: {
    users: number
    customers: number
    jobs: number
    invoices: number
  }
  users?: Array<{
    id: string
    firstName: string
    lastName: string
    email: string
    phone?: string
    role: string
    isActive: boolean
    createdAt: string
  }>
}

interface BusinessStats {
  total: number
  active: number
  inactive: number
  byPlan: Record<string, number>
  byStatus: Record<string, number>
  recentSignups: number
}

const BusinessesPage: React.FC = () => {
  const navigate = useNavigate()
  const [businesses, setBusinesses] = useState<Business[]>([])
  const [stats, setStats] = useState<BusinessStats | null>(null)
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  })
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    plan: '',
    hasBouncedEmails: ''
  })
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null)
  const [businessDrawerVisible, setBusinessDrawerVisible] = useState(false)
  const [createModalVisible, setCreateModalVisible] = useState(false)
  const [form] = Form.useForm()

  const fetchBusinesses = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({
        page: pagination.current.toString(),
        limit: pagination.pageSize.toString(),
        ...(filters.search && { search: filters.search }),
        ...(filters.status && { status: filters.status }),
        ...(filters.plan && { plan: filters.plan }),
        ...(filters.hasBouncedEmails && { hasBouncedEmails: filters.hasBouncedEmails })
      })

      const response = await api.get(`/businesses?${params}`)
      setBusinesses(response.data.businesses)
      setPagination(prev => ({
        ...prev,
        total: response.data.total
      }))
    } catch (error) {
      console.error('Failed to fetch businesses:', error)
      message.error('Failed to load businesses')
    } finally {
      setLoading(false)
    }
  }

  const fetchStats = async () => {
    try {
      const response = await api.get('/businesses/stats')
      setStats(response.data)
    } catch (error) {
      console.error('Failed to fetch stats:', error)
    }
  }

  useEffect(() => {
    fetchBusinesses()
    fetchStats()
  }, [pagination.current, pagination.pageSize, filters])

  const handleSearch = (value: string) => {
    setFilters(prev => ({ ...prev, search: value }))
    setPagination(prev => ({ ...prev, current: 1 }))
  }

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    setPagination(prev => ({ ...prev, current: 1 }))
  }

  const handleTableChange = (paginationConfig: any) => {
    setPagination(prev => ({
      ...prev,
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize
    }))
  }

  const handleToggleStatus = async (businessId: string, isActive: boolean) => {
    try {
      await api.post(`/businesses/${businessId}/toggle-status`, { isActive })
      message.success(`Business ${isActive ? 'activated' : 'deactivated'} successfully`)
      fetchBusinesses()
    } catch (error) {
      console.error('Failed to toggle business status:', error)
      message.error('Failed to update business status')
    }
  }

  const handleViewBusiness = async (business: Business) => {
    try {
      const response = await api.get(`/businesses/${business.id}`)
      // The API returns the business with all details including users
      setSelectedBusiness(response.data)
      setBusinessDrawerVisible(true)
    } catch (error) {
      console.error('Failed to fetch business details:', error)
      message.error('Failed to load business details')
    }
  }

  const formatAddress = (address: string | any): string => {
    if (!address) return 'Not provided'
    
    // If it's already a string, try to parse it
    if (typeof address === 'string') {
      try {
        const parsed = JSON.parse(address)
        if (typeof parsed === 'object') {
          return formatAddressObject(parsed)
        }
        return address
      } catch {
        return address
      }
    }
    
    // If it's an object, format it
    if (typeof address === 'object') {
      return formatAddressObject(address)
    }
    
    return 'Not provided'
  }

  const formatAddressObject = (addr: any): string => {
    const parts: string[] = []
    
    if (addr.street) parts.push(addr.street)
    if (addr.city) parts.push(addr.city)
    if (addr.state) parts.push(addr.state)
    if (addr.zipCode) parts.push(addr.zipCode)
    if (addr.country) parts.push(addr.country)
    
    return parts.length > 0 ? parts.join(', ') : 'Not provided'
  }

  const handleCreateBusiness = async (values: any) => {
    try {
      await api.post('/businesses', {
        ...values,
        publicListing: values.publicListing === true || values.publicListing === 'true',
      })
      message.success('Business created successfully')
      setCreateModalVisible(false)
      form.resetFields()
      fetchBusinesses()
      fetchStats()
    } catch (error) {
      console.error('Failed to create business:', error)
      message.error('Failed to create business')
    }
  }

  const handleOnboardWarehouse = async (businessId: string) => {
    try {
      await api.post(`/businesses/${businessId}/supplier-network/onboard`, {
        publicListing: true,
        defaultTargetIndustries: ['garage_door'],
      })
      message.success('Business onboarded as warehouse supplier')
      fetchBusinesses()
      if (selectedBusiness?.id === businessId) {
        const response = await api.get(`/businesses/${businessId}`)
        setSelectedBusiness(response.data)
      }
    } catch (error: any) {
      console.error('Failed to onboard warehouse:', error)
      message.error(error?.response?.data?.message || 'Failed to onboard warehouse')
    }
  }

  const getStatusTag = (status: string, isActive: boolean) => {
    if (!isActive) {
      return <Tag color="red">Inactive</Tag>
    }
    
    switch (status) {
      case 'active':
        return <Tag color="green">Active</Tag>
      case 'trialing':
        return <Tag color="blue">Trial</Tag>
      case 'past_due':
        return <Tag color="orange">Past Due</Tag>
      case 'canceled':
        return <Tag color="red">Canceled</Tag>
      default:
        return <Tag color="default">{status}</Tag>
    }
  }

  const getPlanTag = (plan: string) => {
    const colors = {
      starter: 'blue',
      professional: 'green',
      enterprise: 'purple'
    }
    return <Tag color={colors[plan as keyof typeof colors] || 'default'}>{plan}</Tag>
  }

  const columns = [
    {
      title: 'Business Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: Business) => (
        <div>
          <div style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            {name}
            {record.hasBouncedEmails && (
              <Tag color="orange" icon={<WarningOutlined />} title="Has bounced emails">
                Bounced Emails
              </Tag>
            )}
          </div>
          {record.email && <div style={{ fontSize: '12px', color: '#666' }}>{record.email}</div>}
        </div>
      )
    },
    {
      title: 'Plan',
      dataIndex: 'subscriptionPlan',
      key: 'subscriptionPlan',
      render: (plan: string) => getPlanTag(plan)
    },
    {
      title: 'Company',
      dataIndex: 'companyType',
      key: 'companyType',
      render: (companyType?: string) =>
        companyType === 'warehouse' ? (
          <Tag color="geekblue">Warehouse</Tag>
        ) : (
          <Tag>Service</Tag>
        ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (record: Business) => getStatusTag(record.subscriptionStatus, record.isActive)
    },
    {
      title: 'Users',
      key: 'users',
      render: (record: Business) => `${record._count.users}/${record.userLimit}`
    },
    {
      title: 'Activity',
      key: 'activity',
      render: (record: Business) => (
        <div style={{ fontSize: '12px' }}>
          <div>Jobs: {record._count.jobs}</div>
          <div>Customers: {record._count.customers}</div>
        </div>
      )
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString()
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: Business) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleViewBusiness(record)}
          />
          <Button
            type="text"
            icon={<SettingOutlined />}
            onClick={() => navigate(`/businesses/${record.id}/settings`)}
            title="Business Settings"
          />
          <Button
            type="text"
            icon={record.isActive ? <StopOutlined /> : <PlayCircleOutlined />}
            onClick={() => handleToggleStatus(record.id, !record.isActive)}
            style={{ color: record.isActive ? '#ff4d4f' : '#52c41a' }}
          />
        </Space>
      )
    }
  ]

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1>Business Management</h1>
        <p>Manage all businesses and their settings</p>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <Row gutter={16} style={{ marginBottom: '24px' }}>
          <Col span={6}>
            <Card>
              <Statistic title="Total Businesses" value={stats.total} />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic title="Active" value={stats.active} valueStyle={{ color: '#3f8600' }} />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic title="Inactive" value={stats.inactive} valueStyle={{ color: '#cf1322' }} />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic title="Recent Signups" value={stats.recentSignups} valueStyle={{ color: '#1890ff' }} />
            </Card>
          </Col>
        </Row>
      )}

      {/* Filters */}
      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={16} align="middle">
          <Col span={8}>
            <Search
              placeholder="Search businesses..."
              allowClear
              onSearch={handleSearch}
              style={{ width: '100%' }}
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder="Status"
              allowClear
              style={{ width: '100%' }}
              onChange={(value) => handleFilterChange('status', value || '')}
            >
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Plan"
              allowClear
              style={{ width: '100%' }}
              onChange={(value) => handleFilterChange('plan', value || '')}
            >
              <Option value="starter">Starter</Option>
              <Option value="professional">Professional</Option>
              <Option value="enterprise">Enterprise</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Bounced Emails"
              allowClear
              style={{ width: '100%' }}
              onChange={(value) => handleFilterChange('hasBouncedEmails', value || '')}
            >
              <Option value="true">Has Bounced</Option>
              <Option value="false">No Bounces</Option>
            </Select>
          </Col>
          <Col span={4} style={{ textAlign: 'right' }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreateModalVisible(true)}
            >
              Create Business
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Businesses Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={businesses}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} businesses`
          }}
          onChange={handleTableChange}
        />
      </Card>

      {/* Business Details Drawer */}
      <Drawer
        title="Business Details"
        width={700}
        open={businessDrawerVisible}
        onClose={() => setBusinessDrawerVisible(false)}
      >
        {selectedBusiness && (
          <div>
            <Card title="Business Information" style={{ marginBottom: '16px' }}>
              <Descriptions column={1} bordered>
                <Descriptions.Item label="Type">
                  <Tag color={selectedBusiness.isActive ? 'green' : 'red'}>
                    {selectedBusiness.isActive ? 'Active Business' : 'Inactive Business'}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  {getStatusTag(selectedBusiness.subscriptionStatus, selectedBusiness.isActive)}
                </Descriptions.Item>
                <Descriptions.Item label="Business Name">
                  {selectedBusiness.name}
                </Descriptions.Item>
                <Descriptions.Item label="Company type">
                  <Space>
                    {selectedBusiness.companyType === 'warehouse' ? (
                      <Tag color="geekblue">Warehouse / supplier</Tag>
                    ) : (
                      <Tag>Service business</Tag>
                    )}
                    {selectedBusiness.companyType !== 'warehouse' && (
                      <Button
                        size="small"
                        type="link"
                        onClick={() => handleOnboardWarehouse(selectedBusiness.id)}
                      >
                        Onboard as warehouse
                      </Button>
                    )}
                  </Space>
                </Descriptions.Item>
                {selectedBusiness.industryType && (
                  <Descriptions.Item label="Industry/Service Type">
                    <Tag color="blue">{selectedBusiness.industryType}</Tag>
                  </Descriptions.Item>
                )}
                {selectedBusiness.serviceCategories && selectedBusiness.serviceCategories.length > 0 && (
                  <Descriptions.Item label="Service Categories">
                    <Space wrap>
                      {selectedBusiness.serviceCategories.map((cat, idx) => (
                        <Tag key={idx} color="cyan">{cat}</Tag>
                      ))}
                    </Space>
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="Business Email">
                  <Space>
                    <MailOutlined />
                    {selectedBusiness.email || 'Not provided'}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="Business Phone">
                  <Space>
                    <PhoneOutlined />
                    {selectedBusiness.phone || 'Not provided'}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="Website">
                  {selectedBusiness.website ? (
                    <a href={selectedBusiness.website} target="_blank" rel="noopener noreferrer">
                      {selectedBusiness.website}
                    </a>
                  ) : 'Not provided'}
                </Descriptions.Item>
                <Descriptions.Item label="Address">
                  <Space>
                    <EnvironmentOutlined />
                    {formatAddress(selectedBusiness.address || (selectedBusiness.city && {
                      street: selectedBusiness.address,
                      city: selectedBusiness.city,
                      state: selectedBusiness.state,
                      zipCode: selectedBusiness.zipCode,
                      country: selectedBusiness.country
                    }))}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="Subscription Plan">
                  {getPlanTag(selectedBusiness.subscriptionPlan)}
                </Descriptions.Item>
                <Descriptions.Item label="User Limit">
                  {selectedBusiness.userLimit}
                </Descriptions.Item>
                <Descriptions.Item label="Created">
                  {new Date(selectedBusiness.createdAt).toLocaleString()}
                </Descriptions.Item>
                <Descriptions.Item label="Last Updated">
                  {new Date(selectedBusiness.updatedAt).toLocaleString()}
                </Descriptions.Item>
                {selectedBusiness.subscriptionEndsAt && (
                  <Descriptions.Item label="Subscription Ends">
                    {new Date(selectedBusiness.subscriptionEndsAt).toLocaleString()}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>

            {/* Users Section */}
            {selectedBusiness.users && selectedBusiness.users.length > 0 && (
              <Card title="Users" style={{ marginBottom: '16px' }}>
                {selectedBusiness.users.map((user, index) => (
                  <div 
                    key={user.id} 
                    style={{ 
                      padding: '12px', 
                      backgroundColor: index % 2 === 0 ? '#fafafa' : '#fff',
                      borderBottom: index < selectedBusiness.users!.length - 1 ? '1px solid #f0f0f0' : 'none',
                      marginBottom: index < selectedBusiness.users!.length - 1 ? '8px' : 0
                    }}
                  >
                    <Row gutter={[16, 8]}>
                      <Col span={8}>
                        <strong>Name:</strong>
                        <div>
                          <UserOutlined style={{ marginRight: '4px' }} />
                          {user.firstName} {user.lastName}
                        </div>
                      </Col>
                      <Col span={8}>
                        <strong>Email:</strong>
                        <div>
                          <MailOutlined style={{ marginRight: '4px' }} />
                          {user.email}
                        </div>
                      </Col>
                      <Col span={8}>
                        <strong>Phone:</strong>
                        <div>
                          {user.phone ? (
                            <>
                              <PhoneOutlined style={{ marginRight: '4px' }} />
                              {user.phone}
                            </>
                          ) : 'N/A'}
                        </div>
                      </Col>
                      <Col span={8}>
                        <strong>Role:</strong>
                        <div>
                          <Tag color={user.role === 'SUPER_ADMIN' || user.role === 'OWNER' ? 'gold' : 'blue'}>
                            {user.role}
                          </Tag>
                        </div>
                      </Col>
                      <Col span={8}>
                        <strong>Status:</strong>
                        <div>
                          <Tag color={user.isActive ? 'green' : 'red'}>
                            {user.isActive ? 'Active' : 'Inactive'}
                          </Tag>
                        </div>
                      </Col>
                      <Col span={8}>
                        <strong>Joined:</strong>
                        <div>{new Date(user.createdAt).toLocaleDateString()}</div>
                      </Col>
                    </Row>
                  </div>
                ))}
              </Card>
            )}

            <Card title="Activity Summary" style={{ marginBottom: '16px' }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Statistic title="Users" value={selectedBusiness._count.users} />
                </Col>
                <Col span={12}>
                  <Statistic title="Customers" value={selectedBusiness._count.customers} />
                </Col>
              </Row>
              <Row gutter={16} style={{ marginTop: '16px' }}>
                <Col span={12}>
                  <Statistic title="Jobs" value={selectedBusiness._count.jobs} />
                </Col>
                <Col span={12}>
                  <Statistic title="Invoices" value={selectedBusiness._count.invoices} />
                </Col>
              </Row>
            </Card>

            {selectedBusiness.hasBouncedEmails && (
              <Card 
                title={
                  <span>
                    <WarningOutlined style={{ color: '#ff4d4f', marginRight: '8px' }} />
                    Email Bounce Status
                  </span>
                }
                style={{ marginBottom: '16px', borderColor: '#ff4d4f' }}
              >
                <p style={{ color: '#ff4d4f', margin: 0 }}>
                  <strong>Warning:</strong> This business has bounced emails. 
                  This may indicate email delivery issues that need attention.
                </p>
              </Card>
            )}
          </div>
        )}
      </Drawer>

      {/* Create Business Modal */}
      <Modal
        title="Create New Business"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false)
          form.resetFields()
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateBusiness}
        >
          <Form.Item
            name="name"
            label="Business Name"
            rules={[{ required: true, message: 'Please enter business name' }]}
          >
            <Input placeholder="Enter business name" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[{ type: 'email', message: 'Please enter a valid email' }]}
          >
            <Input placeholder="Enter email address" />
          </Form.Item>

          <Form.Item
            name="phone"
            label="Phone"
          >
            <Input placeholder="Enter phone number" />
          </Form.Item>

          <Form.Item
            name="subscriptionPlan"
            label="Subscription Plan"
            initialValue="starter"
          >
            <Select>
              <Option value="starter">Starter</Option>
              <Option value="professional">Professional</Option>
              <Option value="enterprise">Enterprise</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="companyType"
            label="Company type"
            initialValue="service"
            extra="Warehouse suppliers can publish catalogs on the Parts Network (including API-synced catalogs)."
          >
            <Select>
              <Option value="service">Service business</Option>
              <Option value="warehouse">Warehouse / supplier</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="industryType"
            label="Industry type"
            initialValue="garage_door"
          >
            <Select>
              <Option value="garage_door">Garage door</Option>
              <Option value="home_services">Home services</Option>
              <Option value="hvac">HVAC</Option>
              <Option value="plumbing">Plumbing</Option>
              <Option value="electrical">Electrical</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="publicListing"
            label="Public Parts Network listing"
            initialValue={false}
            extra="Only applies when company type is Warehouse."
          >
            <Select>
              <Option value={false}>Invite-only (linked buyers)</Option>
              <Option value={true}>Public listing for matching industries</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="userLimit"
            label="User Limit"
            initialValue={3}
          >
            <Input type="number" min={1} />
          </Form.Item>

          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button onClick={() => {
                setCreateModalVisible(false)
                form.resetFields()
              }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Create Business
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default BusinessesPage
